import { memo } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';

import DebouncedColorInput from './DebouncedColorInput';
import { DEFAULT_CUSTOM_PRICE_LABEL } from './matrixClassification';
import {
	Section,
	SectionTitle,
	EditorGrid,
	EditorColumn,
	SimpleClassCardsGrid,
	SimpleClassCard,
	SimpleClassHeader,
	RangeSliderContainer,
	RangeSlider,
	PriceInput,
	SummaryCard,
	SummaryRow,
	SummaryLabel,
	SummaryValue,
	SummaryDetailText,
	EditableLabelInput,
	TotalAreaRow,
	TotalRow,
	ColorDot,
} from './StyledMatrixFilterComponents';

const priceInputStyle = { width: '65px' };
const EMPTY_CUSTOM_PRICE_FIELDS = [];

/**
 * Infer a split point from existing class intervals and build a new open-ended class.
 * Assumption: classes are contiguous ranges with consistent step sizes.
 * Falls back to lastClass.min + 10 if only one class or step can't be inferred.
 */
function buildNextClass(classes, lastClass) {
	let splitPoint;
	if (lastClass && (lastClass.max === null || lastClass.max === undefined)) {
		const secondToLast = classes[classes.length - 2];
		const step =
			secondToLast && lastClass.min > secondToLast.min
				? lastClass.min - secondToLast.min
				: 10;
		splitPoint = lastClass.min + step;
	} else {
		splitPoint = lastClass?.max ?? 0;
	}
	return {
		id: `class_${crypto.randomUUID()}`,
		label: `Class ${classes.length + 1}`,
		color: '#808080',
		opacity: 0.7,
		pricePerM2: 0,
		min: splitPoint,
		max: null,
	};
}

/**
 * Cap the last class's max at the new class's min if it was open-ended.
 * Returns a new array (does not mutate).
 */
function capLastClass(classes, lastClass, splitPoint) {
	const updated = [...classes];
	if (lastClass && (lastClass.max === null || lastClass.max === undefined)) {
		updated[updated.length - 1] = { ...lastClass, max: splitPoint };
	}
	return updated;
}

/**
 * SimpleEditor — the Simple (1D) filter editor panel.
 *
 * Rendered inside the Offcanvas drawer when filter.type === 'simple'.
 * Memoized to avoid re-renders when only the matrix-specific state changes.
 *
 * @param {Object} props
 * @param {Object} props.filter - Current simple filter configuration
 * @param {Function} props.updateFilter - Stable callback to merge updates into filter state
 * @param {Object} props.classificationResults - Per-class classification results (for counts display)
 * @param {Object} props.currency - Resolved currency object ({ id, symbol, label })
 * @param {Array} props.currencies - Full list of available currencies for dropdown
 * @param {{ min: number, max: number, hasData: boolean, coverage: number }} props.propertyRange - Data range for the selected property
 * @param {Array} props.availableProperties - Properties that exist in the hexagon data (for dropdown)
 * @param {string} props.projectNotes - User-entered project notes
 * @param {Function} props.setProjectNotes - Notes state setter
 * @param {boolean} props.isExporting - Whether an export is in progress
 * @param {Function} props.onOpenExportModal - Stable callback to open the export modal
 */
const SimpleEditor = memo(function SimpleEditor({
	filter,
	updateFilter,
	classificationResults,
	currency,
	currencies,
	propertyRange,
	availableProperties,
	projectNotes,
	setProjectNotes,
	isExporting,
	onOpenExportModal,
}) {
	// Compute cost summary totals (single pass over classes)
	const customPriceFields = filter.customPriceFields || EMPTY_CUSTOM_PRICE_FIELDS;
	let areaTotal = 0;
	let classTotal = 0;
	filter.classes.forEach(cls => {
		const area = classificationResults[cls.id]?.totalArea || 0;
		areaTotal += area;
		classTotal += area * (cls.pricePerM2 || 0);
	});
	const customFieldsTotalCost = customPriceFields.reduce(
		(sum, f) => sum + areaTotal * (f.price || 0),
		0
	);
	const grandTotal = classTotal + customFieldsTotalCost;

	const updateCustomField = (idx, patch) => {
		const updated = customPriceFields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
		updateFilter({ customPriceFields: updated });
	};
	const removeCustomField = idx => {
		updateFilter({ customPriceFields: customPriceFields.filter((_, i) => i !== idx) });
	};
	const addCustomField = () => {
		updateFilter({
			customPriceFields: [
				...customPriceFields,
				{ label: DEFAULT_CUSTOM_PRICE_LABEL, price: 0 },
			],
		});
	};

	return (
		<EditorGrid>
			<EditorColumn>
				{/* Property Selection */}
				<Section>
					<SectionTitle>Properties</SectionTitle>
					<Form.Group className="mb-2">
						<Form.Label style={{ fontSize: '0.85rem' }}>Property</Form.Label>
						<Form.Select
							size="sm"
							value={filter.property || 'volume_m3'}
							onChange={e => updateFilter({ property: e.target.value })}>
							{availableProperties.map(p => (
								<option key={p.id} value={p.id}>
									{p.label}
								</option>
							))}
						</Form.Select>
					</Form.Group>
					<Form.Group className="mb-2">
						<Form.Label style={{ fontSize: '0.85rem' }}>Currency</Form.Label>
						<Form.Select
							size="sm"
							value={filter.currency || 'UNIT'}
							onChange={e => updateFilter({ currency: e.target.value })}>
							{currencies.map(c => (
								<option key={c.id} value={c.id}>
									{c.symbol ? `${c.symbol} - ` : ''}
									{c.label}
								</option>
							))}
						</Form.Select>
					</Form.Group>
					<div
						className={`mt-1 ${propertyRange.hasData ? 'text-muted' : 'text-warning'}`}
						style={{ fontSize: '0.75rem' }}>
						{propertyRange.hasData ? (
							<>
								Data range: {propertyRange.min} - {propertyRange.max}{' '}
								{availableProperties.find(p => p.id === filter.property)?.unit ||
									''}
								{propertyRange.coverage < 100 &&
									` (${propertyRange.coverage}% coverage)`}
							</>
						) : (
							'⚠ No data available for this property'
						)}
					</div>
				</Section>
			</EditorColumn>

			<EditorColumn>
				{/* Classes with sliders */}
				<Section>
					<SectionTitle>Classes & Ranges</SectionTitle>
					<SimpleClassCardsGrid>
						{filter.classes?.map((cls, i) => {
							const r = classificationResults[cls.id];
							return (
								<SimpleClassCard key={cls.id}>
									{filter.classes.length > 1 && (
										<Button
											variant="link"
											size="sm"
											className="text-danger p-0"
											style={{
												position: 'absolute',
												top: '6px',
												right: '8px',
												zIndex: 1,
											}}
											onClick={() => {
												const newClasses = filter.classes.filter(
													(_, idx) => idx !== i
												);
												updateFilter({ classes: newClasses });
											}}>
											✕
										</Button>
									)}
									<SimpleClassHeader>
										<DebouncedColorInput
											value={cls.color}
											opacity={cls.opacity || 0.7}
											onChange={color => {
												const newClasses = [...filter.classes];
												newClasses[i] = { ...newClasses[i], color };
												updateFilter({ classes: newClasses });
											}}
											onOpacityChange={opacity => {
												const newClasses = [...filter.classes];
												newClasses[i] = { ...newClasses[i], opacity };
												updateFilter({ classes: newClasses });
											}}
										/>
										<input
											type="text"
											value={cls.label}
											onChange={e => {
												const newClasses = [...filter.classes];
												newClasses[i] = {
													...newClasses[i],
													label: e.target.value,
												};
												updateFilter({ classes: newClasses });
											}}
											style={{
												flex: 1,
												background: 'transparent',
												border: '1px solid var(--bs-border-color)',
												borderRadius: '4px',
												padding: '4px 8px',
												color: 'var(--bs-body-color)',
												paddingRight: '24px',
												fontSize: '0.85rem',
											}}
										/>
										<span className="text-muted">{r?.count || 0} hexagons</span>
									</SimpleClassHeader>

									{/* Min slider with manual input */}
									<RangeSliderContainer>
										<span style={{ width: '30px', fontSize: '0.85rem' }}>
											Min:
										</span>
										<RangeSlider
											type="range"
											$color={cls.color}
											min={propertyRange.min}
											max={propertyRange.max}
											step={
												(propertyRange.max - propertyRange.min) / 100 || 0.1
											}
											value={cls.min ?? propertyRange.min}
											onChange={e => {
												const newClasses = [...filter.classes];
												newClasses[i] = {
													...newClasses[i],
													min: parseFloat(e.target.value),
												};
												updateFilter({ classes: newClasses });
											}}
										/>
										<input
											type="number"
											step="0.1"
											value={cls.min ?? ''}
											onChange={e => {
												const newClasses = [...filter.classes];
												const raw = e.target.value;
												newClasses[i] = {
													...newClasses[i],
													min: raw === '' ? null : parseFloat(raw),
												};
												updateFilter({ classes: newClasses });
											}}
											style={{
												width: '65px',
												background: 'transparent',
												border: '1px solid var(--bs-border-color)',
												borderRadius: '4px',
												padding: '2px 4px',
												textAlign: 'center',
												color: 'var(--bs-body-color)',
												fontSize: '0.85rem',
											}}
										/>
									</RangeSliderContainer>

									{/* Max slider with manual input */}
									<RangeSliderContainer>
										<span style={{ width: '30px', fontSize: '0.85rem' }}>
											Max:
										</span>
										<RangeSlider
											type="range"
											$color={cls.color}
											min={propertyRange.min}
											max={propertyRange.max}
											step={
												(propertyRange.max - propertyRange.min) / 100 || 0.1
											}
											value={cls.max ?? propertyRange.max}
											onChange={e => {
												const newClasses = [...filter.classes];
												const val = parseFloat(e.target.value);
												newClasses[i] = {
													...newClasses[i],
													max: val >= propertyRange.max ? null : val,
												};
												updateFilter({ classes: newClasses });
											}}
										/>
										<input
											type="number"
											step="0.1"
											placeholder="∞"
											value={cls.max ?? ''}
											onChange={e => {
												const newClasses = [...filter.classes];
												if (e.target.value === '') {
													newClasses[i] = { ...newClasses[i], max: null };
												} else {
													newClasses[i] = {
														...newClasses[i],
														max: parseFloat(e.target.value),
													};
												}
												updateFilter({ classes: newClasses });
											}}
											style={{
												width: '65px',
												background: 'transparent',
												border: '1px solid var(--bs-border-color)',
												borderRadius: '4px',
												padding: '2px 4px',
												textAlign: 'center',
												color: 'var(--bs-body-color)',
												fontSize: '0.85rem',
											}}
										/>
									</RangeSliderContainer>

									{/* Price input */}
									<div className="d-flex align-items-center gap-2 mt-2">
										<span style={{ fontSize: '0.85rem' }}>
											Price/{currency.symbol || 'unit'}:
										</span>
										<PriceInput
											type="number"
											step="0.01"
											value={cls.pricePerM2 ?? ''}
											placeholder="0.00"
											onChange={e => {
												const newClasses = [...filter.classes];
												const raw = e.target.value;
												newClasses[i] = {
													...newClasses[i],
													pricePerM2: raw === '' ? 0 : parseFloat(raw),
												};
												updateFilter({ classes: newClasses });
											}}
										/>
									</div>
								</SimpleClassCard>
							);
						})}
						<Button
							variant="outline-secondary"
							size="sm"
							style={{
								width: '280px',
								minWidth: '240px',
								height: 'auto',
								alignSelf: 'stretch',
								minHeight: '80px',
							}}
							onClick={() => {
								const classes = filter.classes;
								const lastClass = classes[classes.length - 1];
								const newClass = buildNextClass(classes, lastClass);
								const updatedClasses = capLastClass(classes, lastClass, newClass.min);
								updateFilter({ classes: [...updatedClasses, newClass] });
							}}>
							+ Add class
						</Button>
					</SimpleClassCardsGrid>
				</Section>
			</EditorColumn>

			{/* Summary Column */}
			<EditorColumn>
				<Section>
					<SectionTitle>Cost Summary</SectionTitle>
					<SummaryCard>
						<Card.Body style={{ padding: '12px' }}>
							<TotalAreaRow>
								<span>Total area</span>
								<span>{areaTotal.toLocaleString()}</span>
							</TotalAreaRow>
							{filter.classes.map(cls => {
								const r = classificationResults[cls.id];
								const area = r?.totalArea || 0;
								const price = cls.pricePerM2 || 0;
								const cost = area * price;
								return (
									<SummaryRow key={cls.id}>
										<SummaryLabel>
											<ColorDot color={cls.color} />
											<span>
												{cls.label}
												<SummaryDetailText>
													{area.toLocaleString()} × {price.toFixed(2)} {currency.symbol}/m²
												</SummaryDetailText>
											</span>
										</SummaryLabel>
										<SummaryValue>
											{cost > 0
												? `${cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency.symbol}`
												: '-'}
										</SummaryValue>
									</SummaryRow>
								);
							})}
							{classificationResults.unclassified?.count > 0 && (
								<SummaryRow>
									<SummaryLabel>
										<ColorDot color="#646464" />
										Unclassified
									</SummaryLabel>
									<SummaryValue>
										{classificationResults.unclassified.count} (
										{classificationResults.unclassified.areaPercentage}%)
									</SummaryValue>
								</SummaryRow>
							)}
							{/* Custom price fields — dynamic list of additional cost lines */}
							{customPriceFields.map((field, idx) => (
									<SummaryRow key={idx}>
										<SummaryLabel>
											<EditableLabelInput
												type="text"
												value={field.label}
												onChange={e => updateCustomField(idx, { label: e.target.value })}
												aria-label={`Custom price label ${idx + 1}`}
												title="Click to edit label"
												maxLength={40}
											/>
										</SummaryLabel>
										<SummaryValue>
											<div className="d-flex align-items-center gap-1">
												<PriceInput
													type="number"
													step="0.01"
													value={field.price ?? ''}
													placeholder="0.00"
													aria-label={`Custom price per square meter ${idx + 1}`}
													title="Additional custom price per m² applied to total area"
													onChange={e => {
														const raw = e.target.value;
														updateCustomField(idx, { price: raw === '' ? 0 : parseFloat(raw) });
													}}
													style={priceInputStyle}
												/>
												<span
													className="text-muted"
													style={{ fontSize: '0.75rem' }}>
													{currency.symbol}/m²
												</span>
												<Button
													variant="link"
													className="text-danger p-0 ms-1"
													style={{ fontSize: '0.75rem', lineHeight: 1 }}
													title="Remove custom price"
													onClick={() => removeCustomField(idx)}>
													×
												</Button>
											</div>
										</SummaryValue>
									</SummaryRow>
							))}
							{customPriceFields.length > 0 && customFieldsTotalCost > 0 && (
								<SummaryRow>
									<SummaryLabel style={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
										Custom prices subtotal
									</SummaryLabel>
									<SummaryValue style={{ fontSize: '0.8rem' }}>
										{customFieldsTotalCost.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}{' '}
										{currency.symbol}
									</SummaryValue>
								</SummaryRow>
							)}
							<Button
								variant="link"
								size="sm"
								className="p-0 mt-2 mb-2"
								style={{ fontSize: '0.85rem', textDecoration: 'none' }}
								onClick={addCustomField}>
								+ Add custom price
							</Button>
							<TotalRow>
								<span>Total</span>
								<span>
									{grandTotal.toLocaleString(undefined, {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})}{' '}
									{currency.symbol}
								</span>
							</TotalRow>
						</Card.Body>
					</SummaryCard>
				</Section>

				<Section>
					<SectionTitle>Project Notes (optional)</SectionTitle>
					<Form.Control
						as="textarea"
						rows={3}
						placeholder="Add notes about the project, calculations, or observations..."
						value={projectNotes}
						onChange={e => setProjectNotes(e.target.value)}
						style={{ fontSize: '0.85rem' }}
					/>
					<small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
						Notes will be included in exported reports.
					</small>
				</Section>

				{/* Export Button */}
				<Button
					variant="success"
					size="sm"
					className="w-100 mt-2"
					style={{ backgroundColor: '#14743B', borderColor: '#14743B' }}
					onClick={onOpenExportModal}
					disabled={isExporting}>
					{isExporting ? '⏳ Generating...' : '📄 Export Report'}
				</Button>
			</EditorColumn>
		</EditorGrid>
	);
});

export default SimpleEditor;
